/**
 * Sign in.
 *
 * Deliberately plain. The interesting decisions are on the server — Argon2id, rotating
 * refresh tokens with reuse detection, uniform messages so an attacker cannot enumerate
 * accounts. This screen's job is to not undo any of them.
 *
 * The error shown is whatever the API said, unchanged. The API returns the same message for
 * "no such account" and "wrong password" on purpose; a helpful client that distinguished
 * them would hand back the enumeration oracle the server just closed.
 */
import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useSession } from '@/app/session'

export function SignIn() {
  const { t } = useTranslation()
  const signIn = useSession((state) => state.signIn)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [organisation, setOrganisation] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await signIn(email, password, organisation.trim() || undefined)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.detail : t('auth.failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="signin" data-testid="signin">
      <form className="signin__card" onSubmit={submit} noValidate>
        <p className="signin__kicker">{t('app.name')}</p>
        <h1 className="signin__title">{t('auth.welcome')}</h1>

        <label className="field">
          <span className="field__label">{t('auth.email')}</span>
          <input
            className="field__input"
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">{t('auth.password')}</span>
          <input
            className="field__input"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        {/*
          The hint is linked with aria-describedby rather than nested inside the label.
          Inside, it becomes part of the field's accessible *name* — a screen reader would
          announce this input as "Organisation Only needed if your email belongs to more
          than one organisation", which is a sentence, not a name. Described-by is
          announced after the name, as help, which is what it is.
        */}
        <div className="field">
          <label className="field__label" htmlFor="org_slug">
            {t('auth.organisation')}
          </label>
          <input
            id="org_slug"
            className="field__input"
            type="text"
            name="org_slug"
            autoComplete="organization"
            aria-describedby="org_slug_hint"
            value={organisation}
            onChange={(event) => setOrganisation(event.target.value)}
          />
          <span className="field__hint" id="org_slug_hint">
            {t('auth.organisationHint')}
          </span>
        </div>

        {error ? (
          <p className="signin__error" role="alert" data-testid="signin-error">
            {error}
          </p>
        ) : null}

        <button className="button button--primary" type="submit" disabled={busy}>
          {busy ? t('auth.signingIn') : t('auth.signIn')}
        </button>
      </form>
    </main>
  )
}
